import React, { useEffect, useRef } from "react";
import { Container, Graphics } from "@pixi/react";
import { useSelector } from "react-redux";
import { ASSET_NAME } from "../../../assets";
import SpineStagesTexture from "../../../Elements/Textures/SpineStagesTexture";
import anime from "animejs";

const WinAnimation = ({ dimensions, symbolSize }) => {
  // Create a ref to access the Graphics component directly
  const graphicsRef = useRef(null);

  // Use Redux's useSelector to extract specific pieces of state from the Redux store
  const { winType } = useSelector((state) => state.game); // Get the type of win from the game state
  const { spinComplete, counterAnimationFinished } = useSelector(
    (state) => state.spin // Get spin completion and counter animation status from spin state
  );
  const { bigWinAnimationStarted, bigWinAnimationCompleted } = useSelector(
    (state) => state.animations // Get big win animation status from animations state
  );

  // useEffect hook to handle side effects related to animation
  useEffect(() => {
    // Check if the graphicsRef is attached to a DOM element
    if (graphicsRef.current) {
      // Create an animation using anime.js
      const animation = anime({
        targets: graphicsRef.current, // Target the current graphics element
        alpha: bigWinAnimationStarted
          ? [0, 1] // Fade in if big win animation has started
          : bigWinAnimationCompleted
          ? [1, 0] // Fade out if big win animation has completed
          : [0, 0], // No change if neither condition is met
        duration: 100, // Duration of the animation in milliseconds
        easing: "easeInOutQuad", // Easing function for smooth transition
        direction: "alternate", // Play the animation in alternate directions
        loop: false, // Do not loop the animation
      });

      // Cleanup function to reverse and play the animation when the component unmounts or dependencies change
      return () => {
        animation.reverse(); // Reverse the animation direction
        animation.play(); // Play the reversed animation
      };
    }
  }, [bigWinAnimationStarted, bigWinAnimationCompleted]); // Dependencies for the useEffect

  // Return the JSX to render the component
  return (
    <Container
      name="BigWinBackground" // Name of the container for identification
      width={dimensions.width} // Set the width of the container
      height={dimensions.height} // Set the height of the container
      x={-dimensions.width / 2} // Position the container horizontally centered
      y={-dimensions.height / 2} // Position the container vertically centered
      sortChildren // Enable sorting of children by zIndex
      zIndex={4} // Set the zIndex for layering
    >
      <Graphics
        ref={graphicsRef} // Attach the ref to the Graphics component
        width={dimensions.width} // Set the width of the graphics
        height={dimensions.height} // Set the height of the graphics
        draw={(g) => {
          g.clear(); // Clear any previous drawings
          g.beginFill(0x000000, 0.75); // Begin a fill with black color and 75% opacity
          g.drawRect(0, 0, dimensions.width, dimensions.height); // Draw a rectangle covering the entire area
          g.endFill(); // End the fill
        }}
      />
      {winType && spinComplete && !counterAnimationFinished && (
        // Conditionally render the SpineStagesTexture component if winType is defined, spin is complete, and counter animation is not finished
        <SpineStagesTexture
          animationPath={ASSET_NAME.winCelebration} // Path to the win celebration animation asset
          winName={winType} // Pass the win type to the component
          dimensions={dimensions} // Pass the dimensions to the component
          zIndex={5} // Set a higher zIndex to render above the background
          symbolSize={symbolSize} // Pass the symbol size to the component
          x={dimensions.width / 2} // Center the texture horizontally
          y={dimensions.height / 2} // Center the texture vertically
        />
      )}
    </Container>
  );
};

// Export the WinAnimation component as the default export
export default WinAnimation;
